import React from 'react';
import { X, FileText, Image as ImageIcon, Code, Brush } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Attachment } from '@/types';

interface InputAttachmentPreviewProps {
  attachments: Attachment[];
  onRemoveAttachment: (id: string) => void;
}

const InputAttachmentPreview: React.FC<InputAttachmentPreviewProps> = ({ 
  attachments, 
  onRemoveAttachment 
}) => {
  if (attachments.length === 0) return null;
  
  return (
    <div className="flex flex-wrap gap-2 p-3 border-t border-x rounded-t-md bg-muted/30">
      {attachments.map((attachment) => (
        <div 
          key={attachment.id} 
          className="relative rounded-md overflow-hidden group border border-border bg-background shadow-sm"
        >
          {attachment.type === 'image' && (
            <div className="w-16 h-16 flex items-center justify-center">
              <img 
                src={attachment.content} 
                alt={attachment.name || "Image attachment"} 
                className="max-h-full max-w-full object-cover"
              />
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-0 right-0 h-5 w-5 rounded-full bg-background/50 hover:bg-background/70 text-foreground opacity-80 hover:opacity-100"
                onClick={() => onRemoveAttachment(attachment.id)}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          )}
          
          {attachment.type === 'file' && (
            <div className="w-16 h-16 flex flex-col items-center justify-center p-1">
              <FileText className="h-8 w-8 text-muted-foreground mb-1" />
              <div className="text-xs truncate w-full text-center text-muted-foreground">
                {attachment.name ? attachment.name.split('.')[0] : 'File'}
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-0 right-0 h-5 w-5 rounded-full bg-background/50 hover:bg-background/70 text-foreground opacity-80 hover:opacity-100"
                onClick={() => onRemoveAttachment(attachment.id)}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          )}
          
          {attachment.type === 'code' && (
            <div className="w-16 h-16 flex flex-col items-center justify-center p-1">
              <Code className="h-8 w-8 text-muted-foreground mb-1" />
              <div className="text-xs truncate w-full text-center text-muted-foreground">
                {attachment.name || 'Code snippet'}
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-0 right-0 h-5 w-5 rounded-full bg-background/50 hover:bg-background/70 text-foreground opacity-80 hover:opacity-100"
                onClick={() => onRemoveAttachment(attachment.id)}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          )}
          
          {attachment.type === 'canvas' && (
            <div className="w-16 h-16 flex items-center justify-center">
              <img 
                src={attachment.content} 
                alt="Canvas drawing" 
                className="max-h-full max-w-full object-cover"
              />
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-0 right-0 h-5 w-5 rounded-full bg-background/50 hover:bg-background/70 text-foreground opacity-80 hover:opacity-100"
                onClick={() => onRemoveAttachment(attachment.id)}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default InputAttachmentPreview;